import React from 'react';
import {Box, Button, Chip, IconButton, InputAdornment, Radio, Switch, TextField, Typography} from '@mui/material';
import {Add, DeleteOutline, KeyboardArrowDown, KeyboardArrowUp, Tune} from '@mui/icons-material';
import {alpha} from '@mui/material/styles';
import {useDispatch, useSelector} from 'react-redux';
import {campaignFormActions as actions} from '../../store/actions/campaignFormAction';
import {FlowSection} from './FlowDesign';

const selectedStyles = (selected, active = true) => ({
    p: 1, borderRadius: '10px', minWidth: 0,
    bgcolor: theme => !active ? alpha(theme.palette.text.primary, 0.05) : selected ? alpha(theme.palette.primary.main, 0.09) : theme.palette.background.paper,
    color: active ? 'text.primary' : 'text.secondary',
    ...(!active && {'& .MuiTextField-root, & .MuiRadio-root, & .flow-rule-name': {opacity: 0.6}})
});

function darkRuleHeaderStyles(theme, selected, active) {
    if (theme.palette.mode !== 'dark') {
        return {};
    }

    return {
        bgcolor: active ? theme.palette.background.darkBlue : alpha(theme.palette.background.darkBlue, 0.55),
        border: '1px solid',
        borderColor: selected && active ? alpha(theme.palette.primary.main, 0.35) : 'transparent',
        '& .flow-rule-name': {
            '&:not(.MuiButton-colorError)': {
                color: selected && active ? 'primary.main' : 'inherit'
            },
            '&:hover': {
                bgcolor: theme.palette.action.hover
            },
            '&.Mui-focusVisible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: 2
            }
        }
    };
}

export default function FlowPaths({getCurrentWeight, validationErrors = []}) {
    const {flow, selectedRuleNumber, selectedPathNumber, selectedRule} = useSelector(state => state.campaignForm);
    const dispatch = useDispatch();
    const errorFor = field => validationErrors.find(issue => issue.field === field)?.message;
    const selectPath = (ruleIndex, pathIndex) => dispatch(actions.updateSelectedPath(ruleIndex, pathIndex, flow.rules[ruleIndex].paths[pathIndex]));
    const removePath = (ruleIndex, pathIndex) => {
        const remainingPath = flow.rules[ruleIndex].paths[pathIndex === 0 ? 1 : 0];
        dispatch(ruleIndex === 0 ? actions.removeDefaultPath(pathIndex) : actions.removeRulePath(ruleIndex, pathIndex));
        dispatch(actions.updateSelectedPath(ruleIndex, 0, remainingPath));
    };
    const removeRule = ruleIndex => {
        dispatch(actions.removeRule(ruleIndex));
        selectPath(0, 0);
    };
    const renderPaths = (rule, ruleIndex) => <Box sx={{display: 'grid', gap: 1, minWidth: 0}}>
        {rule.paths.map((path, pathIndex) => <Box key={pathIndex} className="flow-routing-row flow-path-row"
                                                  data-flow-path={`${ruleIndex}-${pathIndex}`}
                                                  data-active={!!(path.status && rule.status)}
                                                  sx={{
                                                      ...selectedStyles(selectedRule === null && selectedRuleNumber === ruleIndex && selectedPathNumber === pathIndex, !!(path.status && rule.status)), display: 'flex', alignItems: 'center', gap: 0.75,
                                                      ...(validationErrors.some(issue => issue.ruleIndex === ruleIndex && issue.pathIndex === pathIndex) && {outline: '1px solid', outlineColor: 'error.main'})
                                                  }}>
            <Radio size="small" sx={{p: 0.5, flexShrink: 0}}
                   checked={selectedRule === null && selectedRuleNumber === ruleIndex && selectedPathNumber === pathIndex}
                   onChange={() => selectPath(ruleIndex, pathIndex)}
                   slotProps={{input: {'aria-label': `Edit ${rule.name} path ${pathIndex + 1}`}}}/>
            <TextField value={path.name} label="Path name *" size="small" sx={{flex: 1, minWidth: 0}}
                       error={!!errorFor(`rules.${ruleIndex}.paths.${pathIndex}.name`)}
                       helperText={errorFor(`rules.${ruleIndex}.paths.${pathIndex}.name`)}
                       slotProps={{inputLabel: {shrink: true}, htmlInput: {'data-flow-field': `rules.${ruleIndex}.paths.${pathIndex}.name`}}}
                       onFocus={() => selectPath(ruleIndex, pathIndex)}
                       onChange={event => dispatch(ruleIndex === 0 ? actions.updateDefaultPathName(pathIndex, event.target.value) : actions.updateRulePathName(ruleIndex, pathIndex, event.target.value))}/>
            <TextField label="Weight" value={path.weight} size="small" sx={{width: {xs: 88, sm: 104}, flexShrink: 0}}
                       error={!!errorFor(`rules.${ruleIndex}.paths.${pathIndex}.weight`)}
                       helperText={errorFor(`rules.${ruleIndex}.paths.${pathIndex}.weight`) ? 'Use 0–100.' : undefined}
                       slotProps={{
                           inputLabel: {shrink: true}, htmlInput: {inputMode: 'numeric', 'aria-label': `${rule.name} path ${pathIndex + 1} weight`, 'data-flow-field': `rules.${ruleIndex}.paths.${pathIndex}.weight`},
                           input: {
                               endAdornment: <InputAdornment position="end"><Typography
                                   sx={{fontSize: 12}}>{getCurrentWeight(path.weight, rule.paths)}%</Typography></InputAdornment>
                           }
                       }}
                       onChange={event => {
                           const value = /^\d+$/.test(event.target.value) ? parseInt(event.target.value, 10) : '';
                           dispatch(ruleIndex === 0 ? actions.updateDefaultPathWeight(pathIndex, value) : actions.updateRulePathWeight(ruleIndex, pathIndex, value));
                       }}/>
            {rule.paths.length > 1 && <>
                <Switch size="small" sx={{flexShrink: 0}} checked={!!path.status}
                        slotProps={{input: {'aria-label': `Enable ${rule.name} path ${pathIndex + 1}`}}}
                        onChange={event => dispatch(ruleIndex === 0 ? actions.updateDefaultPathStatus(pathIndex, event.target.checked) : actions.updateRulePathStatus(ruleIndex, pathIndex, event.target.checked))}/>
                <IconButton size="small" sx={{p: 0.5, flexShrink: 0}} color="error"
                            aria-label={`Delete ${rule.name} path ${pathIndex + 1}`}
                            onClick={() => removePath(ruleIndex, pathIndex)}><DeleteOutline
                    fontSize="small"/></IconButton>
            </>}
        </Box>)}
        {rule.paths.length < 10 && <Button type="button" startIcon={<Add/>}
                                           onClick={() => dispatch(ruleIndex === 0 ? actions.addDefaultPath() : actions.addRulePath(ruleIndex))}>Add
            path</Button>}
    </Box>;
    return <Box sx={{display: 'grid', gap: 2, minWidth: 0}}>
        <FlowSection title="Default paths" description="Traffic uses these paths when no targeting rule matches.">
            <Chip size="small" label={`${flow.rules[0].paths.length}/10 paths`} sx={{justifySelf: 'start'}}/>
            {renderPaths(flow.rules[0], 0)}
        </FlowSection>
        <FlowSection title="Rule-based paths"
                     description="Rules are evaluated in priority order. Select a rule to edit its conditions.">
            {flow.rules.length === 1 &&
                <Typography sx={{fontSize: 13, color: 'text.secondary'}}>No targeting rules yet. Add a rule to route
                    specific visitors.</Typography>}
            {flow.rules.slice(1).map((rule, index) => {
                const ruleIndex = index + 1;
                const isRuleSelected = selectedRule !== null && selectedRuleNumber === ruleIndex;
                return <Box key={ruleIndex} sx={{minWidth: 0, display: 'grid', gap: 1.5}}>
                    <Box data-flow-rule={ruleIndex} data-active={!!rule.status}
                         sx={[
                             {
                                 ...selectedStyles(isRuleSelected, !!rule.status),
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: 0.5,
                                 ...(errorFor(`rules.${ruleIndex}.name`) && {outline: '1px solid', outlineColor: 'error.main'})
                             },
                             theme => darkRuleHeaderStyles(theme, isRuleSelected, !!rule.status)
                         ]}>
                        <Button type="button" className="flow-rule-name"
                                color={errorFor(`rules.${ruleIndex}.name`) ? 'error' : 'inherit'} startIcon={<Tune/>}
                                title={`${ruleIndex}. ${rule.name}`}
                                aria-pressed={isRuleSelected}
                                onClick={() => dispatch(actions.updateSelectedRule(ruleIndex, rule))}
                                sx={{flex: 1, minWidth: 0, px: 0.75, justifyContent: 'flex-start', textAlign: 'left'}}>
                            <Box component="span"
                                 sx={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{ruleIndex}. {rule.name || 'Enter a rule name'}</Box>
                        </Button>
                        <Box sx={{display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0.25}}>
                            <IconButton size="small" sx={{p: 0.5}} aria-label={`Move rule ${ruleIndex} up`}
                                        disabled={ruleIndex === 1} onClick={() => {
                                dispatch(actions.changeRulePriority(ruleIndex, -1));
                                dispatch(actions.updateSelectedRule(ruleIndex - 1, rule));
                            }}><KeyboardArrowUp fontSize="small"/></IconButton>
                            <IconButton size="small" sx={{p: 0.5}} aria-label={`Move rule ${ruleIndex} down`}
                                        disabled={ruleIndex === flow.rules.length - 1} onClick={() => {
                                dispatch(actions.changeRulePriority(ruleIndex, 1));
                                dispatch(actions.updateSelectedRule(ruleIndex + 1, rule));
                            }}><KeyboardArrowDown fontSize="small"/></IconButton>
                            <Switch size="small" checked={!!rule.status}
                                    slotProps={{input: {'aria-label': `Enable rule ${ruleIndex}`}}}
                                    onChange={event => dispatch(actions.updateRuleStatus(event.target.checked, ruleIndex))}/>
                            <IconButton size="small" sx={{p: 0.5}} color="error" aria-label={`Delete rule ${ruleIndex}`}
                                        onClick={() => removeRule(ruleIndex)}><DeleteOutline
                                fontSize="small"/></IconButton>
                        </Box>
                    </Box>
                    {errorFor(`rules.${ruleIndex}.name`) && <Typography color="error"
                                                                        sx={{fontSize: 12}}>{errorFor(`rules.${ruleIndex}.name`)}</Typography>}
                    {renderPaths(rule, ruleIndex)}
                </Box>;
            })}
            {flow.rules.length < 26 &&
                <Button
                    type="button"
                    variant="outlined"
                    startIcon={<Add/>}
                    onClick={() => dispatch(actions.addRule(flow.rules.length))}
                >
                    Add rule
                </Button>
            }
        </FlowSection>
    </Box>;
}
